import { useState, useEffect, useRef } from "react";
import { DataNode } from "@/shared/data-display/tree";

export type TreeNodeWithValue = DataNode & {
  value: string;
  children?: TreeNodeWithValue[];
};

type UseTreeExpandedKeysOptions = {
  treeData: TreeNodeWithValue[];
  value?: string;
  loading: boolean;
  autoExpandOnMount?: boolean;
};

export function getExpandedKeysByValue(
  tree: TreeNodeWithValue[],
  target?: string,
): string[] {
  if (!target) return [];

  for (const node of tree) {
    if (node.value === target) return [String(node.key)];
    if (node.children) {
      const childPath = getExpandedKeysByValue(node.children, target);
      if (childPath.length) return [String(node.key), ...childPath];
    }
  }
  return [];
}

export function useTreeExpandedKeys({
  treeData,
  value,
  loading,
  autoExpandOnMount = true,
}: UseTreeExpandedKeysOptions) {
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const mounted = useRef(false);

  useEffect(() => {
    if (!mounted.current && autoExpandOnMount && !loading) {
      const keys = value
        ? getExpandedKeysByValue(treeData, value)
        : treeData.map((node) => String(node.key));
      setExpandedKeys(keys);
      mounted.current = true;
    }
  }, [autoExpandOnMount, loading, treeData, value]);

  return { expandedKeys, setExpandedKeys };
}
